/**
 * Customer dashboard. Routes: /dashboard
 * Shows switch to company or admin when user has company or is admin.
 * Renders only content; layout (Navigation, Footer) is provided by DashboardLayout.
 */
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { LayoutDashboard, QrCode, ArrowLeft, Building2, Shield, Receipt, CreditCard, MapPin } from 'lucide-react'
import { useAuthStore } from 'shared/store/authStore'
import { useCompanyRegisterModalStore } from '../../store/companyRegisterModalStore'
import { usePaymentModalStore } from '../../store/paymentModalStore'

export default function DashboardPage() {
  const { t } = useTranslation('portal')
  const me = useAuthStore((s) => s.me)
  const openCompanyRegisterModal = useCompanyRegisterModalStore((s) => s.openModal)
  const openPaymentModal = usePaymentModalStore((s) => s.openModal)
  const hasCompany = !!me?.has_company
  const hasAdmin = !!me?.is_admin

  return (
    <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <div className="w-20 h-20 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <LayoutDashboard className="w-10 h-10 text-blue-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-4">
              {t('dashboard.welcome')} – {t('dashboard.title')}
            </h1>
            <p className="text-slate-600 mb-8">
              {t('dashboard.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-slate-200 text-slate-800 rounded-lg hover:bg-slate-300 transition-colors">
                <ArrowLeft className="w-4 h-4" />
                {t('dashboard.backToHome')}
              </Link>
              <button
                type="button"
                onClick={() => openPaymentModal()}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <QrCode className="w-4 h-4" />
                {t('dashboard.payInvoice')}
              </button>
            </div>

            <div className="grid sm:grid-cols-3 gap-4 mt-10 max-w-2xl mx-auto">
              <Link
                to="/dashboard/payments"
                className="flex items-center gap-3 p-4 rounded-xl border border-slate-200 bg-white hover:border-blue-300 hover:shadow-md transition-all text-left"
              >
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                  <Receipt className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <span className="font-medium text-slate-900">{t('dashboard.myPayments')}</span>
                  <p className="text-sm text-slate-600 mt-0.5">{t('dashboard.previousPayments')}</p>
                </div>
              </Link>
              <Link
                to="/dashboard/cards"
                className="flex items-center gap-3 p-4 rounded-xl border border-slate-200 bg-white hover:border-blue-300 hover:shadow-md transition-all text-left"
              >
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                  <CreditCard className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <span className="font-medium text-slate-900">{t('dashboard.myCards')}</span>
                  <p className="text-sm text-slate-600 mt-0.5">{t('dashboard.manageCards')}</p>
                </div>
              </Link>
              <Link
                to="/dashboard/addresses"
                className="flex items-center gap-3 p-4 rounded-xl border border-slate-200 bg-white hover:border-blue-300 hover:shadow-md transition-all text-left"
              >
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                  <MapPin className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <span className="font-medium text-slate-900">{t('dashboard.myAddresses', 'My Addresses')}</span>
                  <p className="text-sm text-slate-600 mt-0.5">{t('dashboard.manageAddresses', 'Manage billing addresses')}</p>
                </div>
              </Link>
            </div>
          </div>

          {!hasCompany && (
            <div className="mb-8">
              <button
                type="button"
                onClick={openCompanyRegisterModal}
                className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 bg-white hover:border-emerald-300 hover:shadow-md transition-all block w-full text-left"
              >
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
                  <Building2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div className="text-left">
                  <span className="font-medium text-slate-900">{t('dashboard.addCompanyCta')}</span>
                  <p className="text-sm text-slate-600 mt-0.5">{t('dashboard.addCompanyDesc')}</p>
                  <span className="inline-flex items-center gap-1.5 mt-2 text-emerald-600 font-medium text-sm">
                    {t('dashboard.registerCompany')} →
                  </span>
                </div>
              </button>
            </div>
          )}

          {(hasCompany || hasAdmin) && (
            <div className="border-t border-slate-200 pt-8 mt-8">
              <h2 className="text-lg font-semibold text-slate-800 mb-4 text-center">{t('dashboard.switchPanel')}</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {hasCompany && (
                  <Link
                    to="/company"
                    className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 bg-white hover:border-emerald-300 hover:shadow-md transition-all"
                  >
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
                      <Building2 className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div className="text-left">
                      <span className="font-medium text-slate-900">{t('dashboard.switchToCompany')}</span>
                      <p className="text-sm text-slate-600 mt-0.5">{t('dashboard.switchToCompanyDesc')}</p>
                    </div>
                  </Link>
                )}
                {hasAdmin && (
                  <Link
                    to="/admin"
                    className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 bg-white hover:border-blue-300 hover:shadow-md transition-all"
                  >
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                      <Shield className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="text-left">
                      <span className="font-medium text-slate-900">{t('dashboard.switchToAdmin')}</span>
                      <p className="text-sm text-slate-600 mt-0.5">{t('dashboard.switchToAdminDesc')}</p>
                    </div>
                  </Link>
                )}
              </div>
            </div>
          )}
    </div>
  )
}
